import React from 'react';

import App from 'components/app/App';
import {
    HashRouter as Router,
    Redirect,
    Route,
    Switch,
    useHistory,
} from 'react-router-dom';
import Dashboard from 'components/analytics/portfolioSegmentation/dashboard/Dashboard';
import ErrorPage from 'components/error/Error';
import NotFound from 'components/error/NotFound';
import SettingsRoutes from 'routes/settings';
import { clearCookie } from 'utils/cookie';
import urlBuilder from 'utils/urlBuilder';
import Layout from 'components/layout/Layout';
import { ExpirationDashboard } from 'components/leases/expirations';
import AttributesDashboard from 'components/attributes/dashboard/AttributesDashboard';
import { RouteUrls } from 'routes/RouteConstants';
import AnalyticsContainer from 'components/analytics/AnalyticsContainer';
import FinancialOverviewDashboard from 'components/analytics/financialOverview/dashboard/FinancialOverviewDashboard';
import { BalanceSheetOverviewDashboard } from 'components/analytics/balance-sheet-overview/dashboard';
import { FinancialsContainer } from 'components/financials/common';
import { CrosstabContainer } from 'components/financials/crosstab/CrosstabContainer';
import { ComparativeIncomeStatementGrid } from 'components/financials/comparative-income-statement';
import BalanceSheetContainer from 'components/financials/balanceSheet/components/grid/Container';
import RankingContainer from 'components/analytics/ranking/components/RankingDashboard';
import PropertySearch from 'components/property-search/PropertySearch';
import PropertyProfileContainer from 'components/propertyProfile/PropertyProfileContainer';
import { EntityAttributesProvider } from 'contexts';
import TenantsDashboard from 'components/leases/tenants/TenantsDashboard';
import LeasesExpirationDashboard from 'components/leases/expirations/cards/leases-expiration/LeasesExpirationDashboard';
import RentRollDashboard from 'components/leases/expirations/cards/rent-roll/RentRollDashboard';
import { PropertySearchContainer } from 'components/property-search/PropertySearchContainer';
import PlanningHoldSellContainer from 'components/planning/hold-sell/PlanningHoldSellContainer';
import VarianceReportStatusContainer from 'components/financials/variance-report-status/components/VarianceReportStatusContainer';
import VarianceReportingContainer from 'components/financials/variance-report-status/components/VarianceReportingContainer';
import PlanningContainer from 'components/planning/PlanningContainer';
import PlanningCapitalProjectsContainer from 'components/planning/capital-projects/PlanningCapitalProjectsContainer';
import PlanningPrioritiesObjectivesContainer from 'components/planning/priorities-objectives/PlanningPrioritiesObjectivesContainer';
import PlanningServiceContractsContainer from 'components/planning/service-contracts/PlanningServiceContractsContainer';
import ReportDetail from 'components/reports/ReportDetail';
import ReportGrid from 'components/reports/ReportGrid';
import ReportManager from 'components/reports/ReportManager';
import MyReportGrid from 'components/reports/MyReportGrid';
import EntityReportEditorContainer from 'components/reports/EntityReportEditorContainer';
import ReportExportPage from 'components/reports/ReportExportPage';
import { EntityOwnershipProvider } from '../contexts/entity-ownership/EntityOwnershipProvider';
import { connect } from 'react-redux';
import {
    hasOwnershipPercentageEnabled,
    selectCurrentUser,
} from 'state/user/selectors';
import { selectUsersById } from 'state/users/selectors';
import AgedReceivablesContainer from 'components/agedReceivables/AgedReceivablesContainer';
import BudgetUploadContainer from 'components/budgetUpload/BudgetUploadContainer';
import { PermissionsGroupsProvider, AppFeaturePermissions } from 'contexts';

const ProtectedRoute = ({
    component: Component,
    key,
    props,
    ...restOfProps
}) => {
    const isAuthenticated = document.cookie.includes('CLIENT_ID_COOKIE');
    const getLoginUrlWithRedirectBack = () => {
        return window.location.pathname === RouteUrls.LOGIN
            ? window.location.href
            : urlBuilder.addQuery(RouteUrls.LOGIN, {
                  state: window.location.href,
                  idle: true,
              });
    };

    if (!isAuthenticated) {
        clearCookie('XSRF-TOKEN');
        clearCookie('CLIENT_ID_COOKIE');
        // Here we trigger a redirect to the login page which will stop execution of the React code. That's intentional!
        // The login has its own JavaScript and isn't really part of the React app.
        window.location.href = getLoginUrlWithRedirectBack();
    }

    return (
        <Route
            {...restOfProps}
            render={() => {
                if (!isAuthenticated) {
                    return <Redirect to={getLoginUrlWithRedirectBack()} />;
                }

                if (restOfProps.render) {
                    return restOfProps.render();
                }

                return <Component />;
            }}
        />
    );
};

const Routes = ({
    bootstrapped,
    isOwnershipPercentageEnabled,
    userPermissions,
}) => (
    <Router>
        <App bootstrapped={bootstrapped}>
            {bootstrapped && (
                <EntityAttributesProvider>
                    <EntityOwnershipProvider
                        isDisabled={!isOwnershipPercentageEnabled}
                    >
                        <PermissionsGroupsProvider
                            userPermissions={userPermissions}
                        >
                            <Switch>
                                {/* Entry point */}
                                <Route
                                    exact
                                    path="/"
                                    render={() => (
                                        <Redirect to="/search/properties" />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/pdf-render"
                                    render={() => <ReportExportPage />}
                                />

                                {/* Leases Routes */}
                                <ProtectedRoute
                                    key="property-leases"
                                    path={RouteUrls.LEASES_PERFORMANCE_OVERVIEW}
                                    render={() => (
                                        <ExpirationDashboard
                                            delta={'property-leases'}
                                        />
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.LEASES_TENANTS}
                                    render={() => (
                                        <Layout>
                                            <TenantsDashboard />
                                        </Layout>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.LEASES_EXPIRATIONS_SCHEDULE}
                                    render={() => <LeasesExpirationDashboard />}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.LEASE_RENT_ROLL}
                                    render={() => <RentRollDashboard />}
                                />

                                {/* Analytics Routes*/}
                                <ProtectedRoute
                                    path={
                                        RouteUrls.ANALYTICS_FINANCIAL_OVERVIEW
                                    }
                                    render={() => (
                                        <AnalyticsContainer>
                                            <FinancialOverviewDashboard />
                                        </AnalyticsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.ANALYTICS_RANKING}
                                    key="ranking"
                                    render={() => (
                                        <AnalyticsContainer>
                                            <RankingContainer />
                                        </AnalyticsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={
                                        RouteUrls.ANALYTICS_BALANCE_SHEET_OVERVIEW
                                    }
                                    render={() => (
                                        <AnalyticsContainer>
                                            <BalanceSheetOverviewDashboard />
                                        </AnalyticsContainer>
                                    )}
                                />

                                {/* Financials Routes */}
                                <ProtectedRoute
                                    path={
                                        RouteUrls.FINANCIALS_COMPARATIVE_INCOME_STATEMENT
                                    }
                                    render={() => (
                                        <FinancialsContainer>
                                            <ComparativeIncomeStatementGrid />
                                        </FinancialsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.FINANCIALS_BALANCE_SHEET}
                                    render={() => (
                                        <FinancialsContainer>
                                            <BalanceSheetContainer />
                                        </FinancialsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.FINANCIALS_CROSSTAB}
                                    render={() => (
                                        <FinancialsContainer>
                                            <CrosstabContainer />
                                        </FinancialsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={
                                        RouteUrls.FINANCIALS_VARIANCE_REPORT_STATUS
                                    }
                                    render={() => (
                                        <FinancialsContainer>
                                            <VarianceReportStatusContainer />
                                        </FinancialsContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={
                                        RouteUrls.FINANCIALS_VARIANCE_REPORTING
                                    }
                                    render={() => (
                                        <VarianceReportingContainer />
                                    )}
                                />

                                {/* Property Profile Routes */}
                                <ProtectedRoute
                                    path={RouteUrls.PROPERTY_PROFILE_DETAILS}
                                    render={() => <PropertyProfileContainer />}
                                />

                                {/* Planning Routes */}
                                <ProtectedRoute
                                    path={RouteUrls.PLANNING_CAPITAL_TRACKER}
                                    render={() => (
                                        <PlanningContainer>
                                            <PlanningCapitalProjectsContainer />
                                        </PlanningContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.PLANNING_HOLD_SELL}
                                    render={() => (
                                        <PlanningContainer>
                                            <PlanningHoldSellContainer />
                                        </PlanningContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={
                                        RouteUrls.PLANNING_PRIORITIES_AND_OBJECTIVES
                                    }
                                    render={() => (
                                        <PlanningContainer>
                                            <PlanningPrioritiesObjectivesContainer />
                                        </PlanningContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.PLANNING_SERVICE_CONTRACTS}
                                    render={() => (
                                        <PlanningContainer>
                                            <PlanningServiceContractsContainer />
                                        </PlanningContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.AGED_RECEIVABLES}
                                    render={() => <AgedReceivablesContainer />}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.BUDGET_UPLOAD}
                                    render={() => (
                                        <PlanningContainer>
                                            <BudgetUploadContainer />
                                        </PlanningContainer>
                                    )}
                                />

                                <ProtectedRoute
                                    path={`${RouteUrls.MY_REPORT_TEMPLATE}`}
                                    exact
                                    render={() => (
                                        <Layout>
                                            <MyReportGrid />
                                        </Layout>
                                    )}
                                />

                                <ProtectedRoute
                                    path={`${RouteUrls.REPORT_MANAGER}`}
                                    render={() => {
                                        return (
                                            <Layout>
                                                <ReportManager />
                                            </Layout>
                                        );
                                    }}
                                />

                                <ProtectedRoute
                                    path={`${RouteUrls.REPORT_TEMPLATE}`}
                                    render={() => {
                                        return (
                                            <Layout>
                                                <ReportGrid />
                                            </Layout>
                                        );
                                    }}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.REPORT_CREATE}
                                    render={() => <ReportDetail />}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.REPORT_DETAIL}
                                    render={() => <ReportDetail />}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.ENTITY_REPORT_EDITOR}
                                    render={() => (
                                        <EntityReportEditorContainer />
                                    )}
                                />

                                {/* Other Routes */}
                                <ProtectedRoute
                                    path={RouteUrls.ATTRIBUTES}
                                    render={() => <AttributesDashboard />}
                                />

                                <ProtectedRoute
                                    path={RouteUrls.SEARCH}
                                    render={() => (
                                        <PropertySearchContainer>
                                            <PropertySearch />
                                        </PropertySearchContainer>
                                    )}
                                />
                                <ProtectedRoute
                                    path={RouteUrls.SEGMENTATION}
                                    component={Dashboard}
                                />
                                <ProtectedRoute
                                    path={RouteUrls.SETTINGS}
                                    component={SettingsRoutes}
                                />

                                <Route
                                    path="/error"
                                    render={() => (
                                        <ErrorPage>
                                            {' '}
                                            <NotFound />{' '}
                                        </ErrorPage>
                                    )}
                                />
                            </Switch>
                        </PermissionsGroupsProvider>
                    </EntityOwnershipProvider>
                </EntityAttributesProvider>
            )}
        </App>
    </Router>
);

const mapState = function (state) {
    const currentUser = selectCurrentUser(state);
    const currentUserFeaturePermissions = selectUsersById(state, {
        userIds: [currentUser?.id],
    })[0]?.featurePermissions;

    return {
        isOwnershipPercentageEnabled: hasOwnershipPercentageEnabled(state),
        userPermissions: currentUserFeaturePermissions?.length
            ? currentUserFeaturePermissions
            : Object.values(AppFeaturePermissions),
    };
};

export default connect(mapState)(Routes);
